import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface CameraCaptureProps {
  onCapture: (file: File) => void
  onClose: () => void
}

/**
 * In-app camera using getUserMedia — works in Telegram Android WebView
 * where <input type="file" capture="environment"> does not.
 */
export default function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const { t } = useTranslation('common')
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  useEffect(() => {
    let cancelled = false
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play()
            setReady(true)
          }
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('camera.accessDenied'))
      })
    return () => { cancelled = true; stopStream() }
  }, [stopStream])

  const capture = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0)

    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' })
        stopStream()
        onCapture(file)
      },
      'image/jpeg',
      0.85,
    )
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close button */}
      <button
        onClick={() => { stopStream(); onClose() }}
        style={{
          position: 'absolute',
          top: 'calc(12px + var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px))',
          right: 12,
          zIndex: 10,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.5)',
          color: '#fff',
          border: 'none',
          fontSize: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>

      {error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <p style={{ color: '#fff', textAlign: 'center', fontSize: 15 }}>{error}</p>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ flex: 1, objectFit: 'cover', width: '100%' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Shutter button */}
          <div
            style={{
              padding: '20px 0 calc(20px + var(--tg-safe-area-inset-bottom, 24px))',
              display: 'flex',
              justifyContent: 'center',
              background: '#000',
            }}
          >
            <button
              disabled={!ready}
              onClick={capture}
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                border: '4px solid #fff',
                background: ready ? '#fff' : 'rgba(255,255,255,0.3)',
                cursor: ready ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}
